/**
 * content.js — copy for the SEO landing pages + site-wide FAQs
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for the keyword pages: the SeoLanding component
 * renders from here, routes.js builds the static <head> + FAQPage schema
 * from here, so the visible page and the structured data never drift.
 *
 * Every string is { ar, en }. Arabic is the primary (indexed) language.
 * Voice: same as the rest of the site — direct, Saudi-flavored, no fluff.
 */

export const HOME_FAQS = [
  {
    q: { ar: 'وش الفرق بين بطاقة العمل الرقمية والكرت الشخصي الإلكتروني؟', en: 'Is a digital business card the same as an electronic business card?' },
    a: {
      ar: 'نفس الشي. بطاقة عمل رقمية، كرت شخصي إلكتروني، بطاقة أعمال إلكترونية، أو بزنس كارد: كلها تعني كرت عمل يعيش في جوالك بدل الورق. وفي CardPass يكون باس حقيقي في Apple Wallet و Google Wallet يحفظك عند العميل بمسحة QR.',
      en: 'Yes. Digital business card, electronic business card, e-card: all mean a business card that lives on your phone instead of paper. In CardPass it is a real Apple Wallet and Google Wallet pass that clients save with one QR scan.'
    }
  },
  {
    q: { ar: 'كيف أسوّي بطاقة عمل رقمية؟', en: 'How do I create a digital business card?' },
    a: {
      ar: 'سجّل في CardPass بالإيميل أو Google، عبّي اسمك ومسماك وبياناتك، واختر ألوانك وشعارك. خلال 30 ثانية تطلع لك بطاقة حقيقية تعيش في Apple Wallet و Google Wallet، تشاركها بمسح QR — بدون طباعة وبدون تطبيق إضافي.',
      en: 'Sign up to CardPass with email or Google, fill in your name, title, and contact details, then pick your colors and logo. In 30 seconds you get a real pass that lives in Apple Wallet and Google Wallet, shared with a QR scan — no printing, no extra app.'
    }
  },
  {
    q: { ar: 'هل تشتغل البطاقة مع أجهزة أندرويد؟', en: 'Does the card work with Android phones?' },
    a: {
      ar: 'نعم تماماً. لو عندك أندرويد، تقدر تضيف بطاقتك في Google Wallet مباشرة — مثل ما يضيفها صاحب الآيفون في Apple Wallet. وللطرف الآخر: أي كاميرا جوال — آيفون أو أندرويد — تمسح الـ QR وتحفظ بياناتك جهة اتصال، أو تفتح صفحتك العامة. بدون أي تطبيق.',
      en: 'Absolutely. On Android you add your card straight to Google Wallet — just like an iPhone user adds it to Apple Wallet. And for the receiver: any phone camera — iPhone or Android — scans the QR to save your details as a contact, or opens your public page. No app needed.'
    }
  },
  {
    q: { ar: 'كم سعر البطاقة الرقمية؟', en: 'How much does the digital card cost?' },
    a: {
      ar: 'الخطة الأساسية مجانية للأبد وتشمل باس Apple Wallet وQR يحفظ جهة الاتصال فوراً. خلال فترة الإطلاق تفتح لك تجربة 14 يوم لمزايا إضافية مثل الألوان المخصصة والصفحة العامة. بدون بطاقة ائتمانية.',
      en: 'The base plan is free forever and includes the Apple Wallet pass and an instant-save QR. During launch you also get a 14-day trial of extras like custom colors and a public page. No credit card required.'
    }
  },
  {
    q: { ar: 'وش الفرق بين CardPass وبطاقات NFC؟', en: 'What is the difference between CardPass and NFC cards?' },
    a: {
      ar: 'بطاقة NFC قطعة بلاستيك تشتريها وتشيلها معك، وإذا انكسرت أو ضاعت تشتري غيرها. CardPass يعيش في محفظة جوالك: ما ينضاع، يتحدّث تلقائياً عند أي تعديل، ويُشارك بمسح QR يشتغل مع كل الجوالات — مجاناً.',
      en: 'An NFC card is a plastic object you buy and carry; if it breaks or gets lost you buy another. CardPass lives in your phone wallet: it cannot be lost, updates automatically when you edit it, and shares via a QR scan that works with every phone — for free.'
    }
  },
  {
    q: { ar: 'لو غيّرت رقمي أو مسماي الوظيفي، وش يصير؟', en: 'What happens if I change my number or job title?' },
    a: {
      ar: 'تعدّل بياناتك من لوحة التحكم مرة واحدة، وكل بطاقة شاركتها تتحدّث تلقائياً خلال ثواني — حتى البطاقات اللي أضافها ناس ثانين لمحافظهم. هذا الشي مستحيل مع الكروت الورقية وNFC.',
      en: 'Edit your details once on the dashboard, and every card you have ever shared updates automatically within seconds — including passes other people added to their wallets. Impossible with paper or NFC cards.'
    }
  },
  {
    q: { ar: 'هل CardPass مناسب للشركات وفرق العمل؟', en: 'Is CardPass suitable for companies and teams?' },
    a: {
      ar: 'نعم — CardPass للشركات يوحّد هوية شركتك على بطاقة كل موظف، مع لوحة تحكم مركزية تضيف فيها الموظفين (واحد واحد أو من ملف Excel)، وتعدّل بياناتهم أو تحذفهم في أي لحظة، وتلوّن البطاقات حسب المستوى. وفّر تكاليف الطباعة وخلّ بطاقات فريقك تتحدّث لحالها.',
      en: 'Yes — CardPass for Business unifies your brand across every employee card, with a central dashboard to add employees (one by one or from Excel), edit or remove them anytime, and color cards by tier. Cut printing costs and keep your team’s cards up to date automatically.'
    }
  }
];

/** Pricing page FAQ — rendered by Pricing.jsx and baked into its FAQPage schema. */
export const PRICING_FAQS = [
  {
    q: { ar: 'هل أقدر أبدأ مجاناً؟', en: 'Can I start for free?' },
    a: {
      ar: 'نعم. الخطة المجانية فيها باس Apple Wallet مع QR vCard. وخلال الإطلاق التجريبي تنفتح لك مزايا إضافية — ألوان، شعار وصورة، صفحة عامة بـ URL خاص، وسينك تلقائي — كـ«تجربة» مجانية.',
      en: 'Yes. The Free plan includes an Apple Wallet pass with a vCard QR. And during the experimental launch, extra features unlock as a free “trial” — colors, logo & photo, a public page with a custom URL, and auto-sync.'
    }
  },
  {
    q: { ar: 'المزايا المعلّمة بـ «تجربة» كم تستمر؟', en: 'How long do the “trial” features last?' },
    a: {
      ar: 'متاحة مجاناً لمدة 14 يوم من تاريخ إصدار بطاقتك. بعدها تحتاج خطة مدفوعة للاستمرار فيها — وبطاقتك الأساسية تبقى شغّالة مجاناً.',
      en: 'They’re free for 14 days from the date you issue your card. After that you’ll need a paid plan to keep them — and your core card keeps working for free.'
    }
  },
  {
    q: { ar: 'متى تنزل باقتا برو وبرو+؟', en: 'When will Pro and Pro+ launch?' },
    a: {
      ar: 'قريباً، بعد انتهاء مرحلة الإطلاق التجريبي. نعلن الأسعار وطريقة الاشتراك وقتها — تابعنا.',
      en: 'Soon, after the experimental launch ends. We’ll announce pricing and how to subscribe then — stay tuned.'
    }
  },
  {
    q: { ar: 'هل مسح بطاقات العمل موجود في كل الباقات؟', en: 'Is business-card scanning included in every plan?' },
    a: {
      ar: 'نعم. المجاني والبرو يدعمون مسح بطاقة واحدة كل مرة. برو+ يضيف Bulk upload عشان تمسح عدة بطاقات دفعة واحدة بدل بطاقة بطاقة.',
      en: 'Yes. Free and Pro support scanning one card at a time. Pro+ adds bulk upload so you can scan multiple cards at once instead of one-by-one.'
    }
  },
  {
    q: { ar: 'وش يعني الإطلاق تجريبي؟', en: 'What does “experimental launch” mean?' },
    a: {
      ar: 'المنصّة في مرحلة بيتا، فالمزايا والخطط والأسعار قد تتغيّر ونحسّنها باستمرار. بطاقتك وبياناتك تبقى شغّالة، وأي تغيير جوهري نخبرك فيه مسبقاً.',
      en: 'The platform is in beta, so features, plans, and pricing may change as we keep improving. Your card and data keep working, and we’ll notify you ahead of any material change.'
    }
  },
  {
    q: { ar: 'متى أحتاج برو؟', en: 'When do I need Pro?' },
    a: {
      ar: 'لما تبي تثبّت مزايا التجربة بشكل دائم — صفحة عامة باسمك وURL مخصّص، تحليلات، وLead Capture، مع تخصيص كامل بعد انتهاء الـ14 يوم.',
      en: 'When you want to keep the trial features permanently — a public page with your name and a custom URL, analytics, Lead Capture, and full customization after the 14 days end.'
    }
  },
  {
    q: { ar: 'Lead Capture وش يسوي؟', en: 'What does Lead Capture do?' },
    a: {
      ar: 'لما أحد يمسح بطاقتك، يقدر يترك بياناته في فورم صغير. أنت تستلم إشعار ببياناته على إيميلك — رجال مبيعات يحبونها.',
      en: 'When someone scans your card, they can leave their contact via a small form. You get notified by email — sales pros love it.'
    }
  },
  {
    q: { ar: 'وش طرق الدفع المتاحة؟', en: 'What payment methods will you accept?' },
    a: {
      ar: 'عند إطلاق الخطط المدفوعة: مدى، Visa، Mastercard، Apple Pay — عبر بوابة دفع آمنة (Moyasar).',
      en: 'When paid plans launch: mada, Visa, Mastercard, Apple Pay — via the Moyasar secure gateway.'
    }
  }
];

export const SEO_PAGES = {
  'digital-business-card': {
    slug: 'digital-business-card',
    nav: { ar: 'بطاقة العمل الرقمية', en: 'Digital business card' },
    label: { ar: 'الدليل الشامل', en: 'The complete guide' },
    h1: { ar: 'بطاقة العمل الرقمية — بديل الكروت الورقية في السعودية', en: 'The digital business card — the paper-card replacement, built for Saudi' },
    lead: {
      ar: 'بطاقة العمل الرقمية هي نسخة حيّة من بطاقتك الورقية: تعيش في جوالك، تنشارك بمسح QR، وتتحدّث تلقائياً كل ما تغيّرت بياناتك. هذا الدليل يشرح كيف تشتغل، وليش صارت المعيار الجديد في التعارف المهني، وكيف تسوّي بطاقتك خلال 30 ثانية.',
      en: 'A digital business card is a living version of your paper card: it lives on your phone, shares with a QR scan, and updates automatically whenever your details change. This guide covers how it works, why it became the new standard for professional networking, and how to create yours in 30 seconds.'
    },
    sections: [
      {
        h2: { ar: 'وش هي بطاقة العمل الرقمية؟', en: 'What is a digital business card?' },
        paras: [
          {
            ar: 'هي بطاقة تعريف مهنية إلكترونية تحمل اسمك، مسماك الوظيفي، شركتك، وطرق التواصل معك — لكن بدل ما تنطبع على ورق، تعيش في جوالك وتنشارك رقمياً. في CardPass، البطاقة تصدر كـ«باس» حقيقي داخل Apple Wallet: نفس المكان اللي فيه بطاقات الطيران والدفع، فهي دايماً معك ودايماً جاهزة من شاشة القفل.',
            en: 'It is an electronic professional identity card carrying your name, job title, company, and contact details — but instead of being printed on paper, it lives on your phone and is shared digitally. With CardPass, the card is issued as a real pass inside Apple Wallet: the same place as your boarding passes and payment cards, so it is always with you and ready from the lock screen.'
          },
          {
            ar: 'الطرف الآخر ما يحتاج أي تطبيق: يمسح كود QR بكاميرا جواله — آيفون أو أندرويد — وتنحفظ بياناتك عنده كجهة اتصال كاملة، أو يفتح صفحتك العامة اللي تجمع كل روابطك في مكان واحد.',
            en: 'The other person needs no app at all: they scan a QR code with their phone camera — iPhone or Android — and your details are saved as a complete contact, or they open your public page that gathers all your links in one place.'
          }
        ]
      },
      {
        h2: { ar: 'ليش تستبدل الكرت الورقي؟', en: 'Why replace the paper card?' },
        paras: [
          {
            ar: 'الكرت الورقي ينتهي مفعوله لحظة ما تتغيّر أي معلومة فيه، و88% من الكروت الورقية تنرمى خلال أسبوع. البطاقة الرقمية تحل المشكلتين من الجذر:',
            en: 'A paper card expires the moment any detail on it changes, and most paper cards are thrown away within a week. The digital card solves both problems at the root:'
          }
        ],
        bullets: [
          { ar: 'تحديث تلقائي: عدّل رقمك أو مسماك مرة واحدة، وكل بطاقة شاركتها تتحدّث خلال ثواني.', en: 'Auto-update: edit your number or title once, and every card you shared updates within seconds.' },
          { ar: 'ما تنضاع ولا تخلص: ما فيه «نسيت كروتي بالمكتب» — بطاقتك في جوالك، وتنشارك بلا حدود.', en: 'Never lost, never runs out: no more “left my cards at the office” — your card is on your phone and shares without limits.' },
          { ar: 'تنحفظ فعلاً: بدل كرت ينرمى، بياناتك تدخل جهات اتصال الطرف الآخر بمسحة واحدة.', en: 'Actually saved: instead of a card in the trash, your details land in the other person’s contacts with one scan.' },
          { ar: 'صديقة للبيئة وبدون تكلفة طباعة: لا مطابع، لا إعادة طلب، لا هدر.', en: 'Eco-friendly with zero printing cost: no print shops, no reorders, no waste.' },
          { ar: 'انطباع أول احترافي: بطاقة مصممة بألوانك وشعارك تطلع من محفظة جوالك — مو ورقة مجعّدة من الجيب.', en: 'A professional first impression: a card designed with your colors and logo, presented from your phone wallet — not a crumpled paper from your pocket.' }
        ]
      },
      {
        h2: { ar: 'كيف تشتغل في المواقف الحقيقية؟', en: 'How does it work in real situations?' },
        paras: [
          {
            ar: 'في معرض أو فعالية: دبل-كليك على زر الآيفون الجانبي، بطاقتك تطلع من شاشة القفل، والطرف الآخر يصوّر الـ QR — انتهى التبادل في أقل من خمس ثواني. في اجتماع رسمي: تعرض البطاقة أو ترسل رابط صفحتك العامة في الواتساب. ومع عملاء ما قابلتهم: رابطك في توقيع الإيميل وحساباتك يوصلهم لصفحة فيها كل شي.',
            en: 'At an expo or event: double-click the iPhone side button, your card appears from the lock screen, and the other person scans the QR — the exchange is done in under five seconds. In a formal meeting: present the pass or send your public page link on WhatsApp. With clients you never met: your link in your email signature and bios takes them to a page with everything.'
          }
        ]
      },
      {
        h2: { ar: 'مصممة للسوق السعودي والخليجي', en: 'Built for the Saudi and Gulf market' },
        paras: [
          {
            ar: 'CardPass عربي أولاً: بطاقتك وصفحتك العامة تعرض اسمك ومسماك بالعربي والإنجليزي معاً، لأن اجتماعك الأول قد يكون بالعربي والثاني مع شريك أجنبي. التصميم، الاتجاه RTL، وحتى تفاصيل الخط — كلها مبنية لسوقنا من اليوم الأول، مو ترجمة متأخرة لمنتج أجنبي.',
            en: 'CardPass is Arabic-first: your card and public page show your name and title in both Arabic and English, because your first meeting might be in Arabic and the next with a foreign partner. The design, RTL direction, and typography details are all built for our market from day one — not a late translation of a foreign product.'
          }
        ]
      }
    ],
    steps: {
      title: { ar: 'سوّ بطاقتك في ثلاث خطوات', en: 'Create your card in three steps' },
      items: [
        { t: { ar: 'سجّل مجاناً', en: 'Sign up free' }, d: { ar: 'إيميل أو Google — بدون بطاقة ائتمانية.', en: 'Email or Google — no credit card.' } },
        { t: { ar: 'صمّم بطاقتك', en: 'Design your card' }, d: { ar: 'بياناتك، ألوانك، شعارك وصورتك — والمعاينة حيّة.', en: 'Your details, colors, logo and photo — with a live preview.' } },
        { t: { ar: 'أضفها وشارك', en: 'Add & share' }, d: { ar: 'أضفها لـ Apple Wallet وشاركها بمسح QR.', en: 'Add it to Apple Wallet and share with a QR scan.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل أحتاج تطبيق عشان أستخدم البطاقة الرقمية؟', en: 'Do I need an app to use a digital business card?' },
        a: { ar: 'لا. بطاقة CardPass تعيش في Apple Wallet الموجود أصلاً في كل آيفون، والطرف المستقبل يحتاج كاميرا جواله فقط. كل الإدارة تتم من المتصفح.', en: 'No. Your CardPass lives in Apple Wallet, already on every iPhone, and the receiver only needs their phone camera. Everything is managed from the browser.' }
      },
      {
        q: { ar: 'هل البطاقة الرقمية مقبولة رسمياً في بيئة الأعمال؟', en: 'Is a digital card accepted in professional settings?' },
        a: { ar: 'أصبحت هي المعيار: أسرع في التبادل، أدق في الحفظ، وانطباعها أحدث. ولأنها بألوان شركتك وشعارك، تعطي حضوراً مهنياً أقوى من ورقة مطبوعة.', en: 'It has become the standard: faster to exchange, more accurate to save, and a more modern impression. With your company colors and logo, it carries a stronger professional presence than a printed paper.' }
      },
      {
        q: { ar: 'وش يصير لو الطرف الآخر ما عنده إنترنت لحظة المسح؟', en: 'What if the other person has no internet at the moment of scanning?' },
        a: { ar: 'كود الـ QR في وضع «حفظ جهة الاتصال» يحمل البيانات نفسها داخل الكود (vCard)، فالحفظ يشتغل حتى بدون إنترنت.', en: 'In save-contact mode the QR carries the data itself (vCard), so saving works even without internet.' }
      },
      {
        q: { ar: 'أقدر أسوي أكثر من بطاقة؟', en: 'Can I have more than one card?' },
        a: { ar: 'باقة برو+ القادمة تدعم حتى 3 بطاقات بشخصيات مختلفة — مثلاً بطاقة لشغلك الرسمي وأخرى لمشروعك الخاص.', en: 'The upcoming Pro+ plan supports up to 3 cards with different personas — for example one for your job and one for your side business.' }
      }
    ],
    cta: {
      title: { ar: 'جرّب بنفسك — بطاقتك جاهزة خلال 30 ثانية.', en: 'Try it yourself — your card is ready in 30 seconds.' },
      sub: { ar: 'مجاناً، بدون بطاقة ائتمانية، وبالعربي.', en: 'Free, no credit card, Arabic-first.' },
      btn: { ar: 'أنشئ بطاقتك الرقمية', en: 'Create your digital card' }
    },
    related: ['nfc-business-card', 'apple-wallet-business-card', 'qr-code-business-card']
  },

  'nfc-business-card': {
    slug: 'nfc-business-card',
    nav: { ar: 'بطاقة NFC', en: 'NFC business card' },
    label: { ar: 'مقارنة صادقة', en: 'An honest comparison' },
    h1: { ar: 'بطاقة NFC أم بطاقة Apple Wallet؟ المقارنة قبل ما تشتري', en: 'NFC card or Apple Wallet card? Compare before you buy' },
    lead: {
      ar: 'تفكر تطلب بطاقة NFC بلاستيكية؟ فكرة البطاقات الذكية صحيحة — لكن شراء قطعة بلاستيك بـ 150–400 ريال مالها داعي إذا جوالك نفسه يقدر يكون البطاقة. هذي مقارنة عملية بين بطاقات NFC وبطاقة CardPass في Apple Wallet.',
      en: 'Thinking of ordering a plastic NFC card? The smart-card idea is right — but buying a 150–400 SAR piece of plastic makes little sense when your phone itself can be the card. Here is a practical comparison between NFC cards and a CardPass in Apple Wallet.'
    },
    compare: {
      title: { ar: 'المقارنة وجهاً لوجه', en: 'Head-to-head' },
      colA: { ar: 'بطاقة NFC بلاستيكية', en: 'Plastic NFC card' },
      colB: { ar: 'CardPass — Apple Wallet + QR', en: 'CardPass — Apple Wallet + QR' },
      rows: [
        { f: { ar: 'التكلفة', en: 'Cost' }, a: { ar: '150–400 ريال لكل بطاقة', en: '150–400 SAR per card' }, b: { ar: 'مجاناً', en: 'Free' } },
        { f: { ar: 'قطعة مادية تشيلها', en: 'A physical object to carry' }, a: { ar: 'نعم — وتقدر تنضاع أو تنكسر', en: 'Yes — can be lost or broken' }, b: { ar: 'لا — تعيش في جوالك', en: 'No — lives on your phone' } },
        { f: { ar: 'تعديل البيانات بعد الإصدار', en: 'Editing details after issue' }, a: { ar: 'يعتمد على المزوّد، وغالباً برسوم اشتراك', en: 'Depends on the vendor, usually a subscription' }, b: { ar: 'فوري ومجاني — يتحدّث في كل مكان خلال ثواني', en: 'Instant and free — updates everywhere in seconds' } },
        { f: { ar: 'لو نسيتها بالبيت', en: 'If you forget it at home' }, a: { ar: 'رحت عليك — ما فيه بطاقة', en: 'Tough luck — no card today' }, b: { ar: 'مستحيل تنتسي — جوالك دايماً معك', en: 'Cannot be forgotten — your phone is always with you' } },
        { f: { ar: 'استقبال البيانات عند الطرف الآخر', en: 'How the receiver gets your details' }, a: { ar: 'لمسة NFC تفتح رابط (يحتاج إنترنت)', en: 'NFC tap opens a link (needs internet)' }, b: { ar: 'مسح QR يحفظ جهة الاتصال مباشرة — حتى بدون إنترنت', en: 'QR scan saves the contact directly — even offline' } },
        { f: { ar: 'التوافق', en: 'Compatibility' }, a: { ar: 'يعتمد على دعم NFC ومكان الشريحة بالجوال', en: 'Depends on NFC support and antenna position' }, b: { ar: 'أي كاميرا جوال — آيفون وأندرويد', en: 'Any phone camera — iPhone and Android' } },
        { f: { ar: 'الوصول السريع', en: 'Quick access' }, a: { ar: 'تطلعها من المحفظة أو الجيب', en: 'Pull it from your wallet or pocket' }, b: { ar: 'من شاشة قفل الآيفون بدبل-كليك', en: 'From the iPhone lock screen with a double-click' } },
        { f: { ar: 'لفريق كامل', en: 'For a whole team' }, a: { ar: 'شراء وشحن وإعادة برمجة لكل موظف', en: 'Buy, ship, and reprogram per employee' }, b: { ar: 'إصدار وسحب مركزي من لوحة تحكم', en: 'Central issue and revoke from one dashboard' } }
      ]
    },
    sections: [
      {
        h2: { ar: 'متى تكون بطاقة NFC خياراً منطقياً؟', en: 'When does an NFC card make sense?' },
        paras: [
          {
            ar: 'بكل صدق: إذا تحب إحساس القطعة المادية الفاخرة وتعتبرها أداة انطباع بحد ذاتها، بطاقة NFC المعدنية لها حضورها. لكن وظيفياً، كل شي تسويه البطاقة — فتح صفحة، حفظ جهة اتصال — يسويه جوالك بالـ QR أسرع وبدون تكلفة وبدون خطر الضياع. ولهذا أغلب من جرّب الاثنين يستقر على البطاقة اللي في محفظة جواله.',
            en: 'Honestly: if you love the feel of a premium physical object and treat it as a statement piece, a metal NFC card has presence. Functionally though, everything it does — opening a page, saving a contact — your phone does faster with QR, at zero cost and zero risk of loss. That is why most people who try both settle on the card living in their phone wallet.'
          },
          {
            ar: 'وفيه نقطة عملية مهمة: شريحة NFC تنبرمج على رابط، فإذا غيّرت مزوّد الخدمة أو انتهى اشتراكك، البطاقة اللي دفعت فيها مئات الريالات تصير خردة. بطاقة CardPass ما فيها قطعة تشتريها أصلاً — بياناتك ملكك وتتحدّث من لوحة تحكمك.',
            en: 'One practical point: an NFC chip is programmed to a link, so if you switch vendors or your subscription lapses, the card you paid hundreds for becomes scrap. With CardPass there is no object to buy in the first place — your data is yours and updates from your dashboard.'
          }
        ]
      },
      {
        h2: { ar: 'الخلاصة', en: 'The bottom line' },
        paras: [
          {
            ar: 'قبل ما تدفع قيمة بطاقة NFC، جرّب البطاقة المجانية في Apple Wallet. إذا حسيت إنك محتاج القطعة المادية بعدها — على الأقل قرارك صار عن تجربة. والأغلب يكتشف أن الجوال كان البطاقة المثالية من البداية.',
            en: 'Before paying for an NFC card, try the free Apple Wallet card first. If you still want the physical object afterwards — at least the decision came from experience. Most discover the phone was the perfect card all along.'
          }
        ]
      }
    ],
    faqs: [
      {
        q: { ar: 'هل بطاقات NFC أسرع من مسح QR؟', en: 'Is an NFC tap faster than a QR scan?' },
        a: { ar: 'الفرق أجزاء من الثانية. لمسة NFC تتطلب معرفة مكان الشريحة في كل جوال، بينما الـ QR ظاهر ومفهوم للجميع: ترفع الكاميرا، تمسح، تحفظ. وفي الفعاليات، الناس متعودة على QR أكثر بكثير.', en: 'The difference is fractions of a second. An NFC tap requires knowing where the antenna sits on each phone, while QR is visible and universally understood: raise the camera, scan, save. At events, people are far more used to QR.' }
      },
      {
        q: { ar: 'دفعت في بطاقة NFC من قبل — أقدر أنقل لـ CardPass؟', en: 'I already paid for an NFC card — can I switch to CardPass?' },
        a: { ar: 'طبعاً، والانتقال يأخذ دقيقة: تسوي بطاقتك المجانية وتبدأ تشارك فيها فوراً. وبعض العملاء يبرمجون شريحة الـ NFC القديمة على رابط صفحتهم العامة في CardPass فتستفيد من القطعتين.', en: 'Of course, and switching takes a minute: create your free card and start sharing immediately. Some customers even reprogram their old NFC chip to point at their CardPass public page — best of both.' }
      },
      {
        q: { ar: 'هل فيه رسوم شهرية مخفية؟', en: 'Are there hidden monthly fees?' },
        a: { ar: 'الخطة الأساسية مجانية للأبد: باس Apple Wallet + QR حفظ مباشر. المزايا الإضافية (ألوان مخصصة، صفحة عامة بـ URL خاص) ضمن تجربة مجانية 14 يوم، وباقات برو قادمة بأسعار معلنة وواضحة.', en: 'The base plan is free forever: Apple Wallet pass + instant-save QR. Extras (custom colors, a public page with custom URL) come as a 14-day free trial, with clearly priced Pro plans coming soon.' }
      },
      {
        q: { ar: 'شركتنا وزّعت بطاقات NFC للموظفين — وش البديل؟', en: 'Our company issued NFC cards to employees — what is the alternative?' },
        a: { ar: 'CardPass للشركات يصدر بطاقات موحّدة الهوية لكل الفريق من لوحة واحدة: إضافة موظف جديد بثواني، تعديل جماعي فوري، وسحب البطاقة لحظة مغادرة الموظف — بدون شراء أو شحن أو إعادة برمجة.', en: 'CardPass for Business issues brand-unified cards for the whole team from one dashboard: add a new employee in seconds, bulk-edit instantly, and revoke a card the moment someone leaves — no purchasing, shipping, or reprogramming.' }
      }
    ],
    cta: {
      title: { ar: 'وفّر قيمة البطاقة البلاستيكية.', en: 'Save the price of the plastic.' },
      sub: { ar: 'بطاقتك في Apple Wallet — مجاناً وخلال 30 ثانية.', en: 'Your card in Apple Wallet — free, in 30 seconds.' },
      btn: { ar: 'جرّب البديل المجاني', en: 'Try the free alternative' }
    },
    related: ['digital-business-card', 'apple-wallet-business-card', 'qr-code-business-card']
  },

  'apple-wallet-business-card': {
    slug: 'apple-wallet-business-card',
    nav: { ar: 'بطاقة Apple Wallet', en: 'Apple Wallet card' },
    label: { ar: 'المنصة الأصلية', en: 'The native platform' },
    h1: { ar: 'بطاقة أعمال في Apple Wallet — باس حقيقي، مو صورة', en: 'A business card in Apple Wallet — a real pass, not a screenshot' },
    lead: {
      ar: 'Apple Wallet مو بس للطيران والدفع: تقدر تخلي بطاقة أعمالك باس رسمي بجانبهم. CardPass يولّد لك Wallet pass حقيقي — بتقنية الباسات نفسها اللي تستخدمها شركات الطيران — يفتح من شاشة القفل ويتحدّث عبر الإشعارات الفورية.',
      en: 'Apple Wallet is not just for flights and payments: your business card can be an official pass right next to them. CardPass generates a real Wallet pass — the same pass technology airlines use — opening from the lock screen and updating via push.'
    },
    sections: [
      {
        h2: { ar: 'ليش Apple Wallet بالذات؟', en: 'Why Apple Wallet specifically?' },
        paras: [
          {
            ar: 'لأنه المكان الوحيد اللي مضمون إنه «دايماً معك وجاهز فوراً». التطبيقات تنحذف، والروابط تضيع في المحادثات، لكن المحفظة جزء من النظام نفسه: دبل-كليك على الزر الجانبي وبطاقتك قدامك حتى والشاشة مقفلة. ولأن الباس أصلي، يدعم التحديث الفوري: نغيّر البيانات من السيرفر وتوصل بطاقتك المحدثة لكل من أضافها — بدون ما يسوون شي.',
            en: 'Because it is the one place guaranteed to be “always with you and instantly ready.” Apps get deleted and links get buried in chats, but the Wallet is part of the OS itself: double-click the side button and your card is there even with the screen locked. And because the pass is native, it supports live updates: we push changes from the server and the refreshed card reaches everyone who added it — with zero effort on their side.'
          }
        ],
        bullets: [
          { ar: 'فتح فوري من شاشة القفل — أسرع من أي تطبيق.', en: 'Instant access from the lock screen — faster than any app.' },
          { ar: 'تحديث عبر Push: عدّل مرة، تتحدّث عند الجميع.', en: 'Push updates: edit once, it refreshes for everyone.' },
          { ar: 'تصميم باس أصلي بألوانك وشعارك وصورتك.', en: 'A native pass design with your colors, logo, and photo.' },
          { ar: 'QR مدمج في الباس: حفظ جهة اتصال أو فتح صفحتك العامة.', en: 'A QR built into the pass: save-contact or open your public page.' }
        ]
      },
      {
        h2: { ar: 'والطرف الآخر إذا ما عنده آيفون؟', en: 'And if the other person has no iPhone?' },
        paras: [
          {
            ar: 'ما يفرق. البطاقة تعيش في محفظتك أنت — أما المشاركة فتتم بمسح QR يشتغل مع أي كاميرا جوال في العالم. مستخدم الأندرويد يمسح الكود وتنحفظ بياناتك عنده كجهة اتصال، أو تنفتح له صفحتك العامة بالعربي والإنجليزي.',
            en: 'It does not matter. The card lives in your wallet — sharing happens via a QR scan that works with any phone camera in the world. An Android user scans the code and your details are saved as a contact, or your bilingual public page opens for them.'
          }
        ]
      },
      {
        h2: { ar: 'كيف تنشئ بطاقة Apple Wallet لبيانات تواصلك؟', en: 'How do you create an Apple Wallet card for your contact details?' },
        paras: [
          {
            ar: 'ما تحتاج حساب مطوّر ولا شهادات ولا أدوات تصميم. CardPass يتكفل بالجزء التقني كله — التوقيع، التشفير، خدمة التحديث — وأنت تعبّي بياناتك وتختار شكل بطاقتك من المتصفح، وتضغط «أضف إلى Apple Wallet». انتهى.',
            en: 'You need no developer account, certificates, or design tools. CardPass handles the entire technical layer — signing, encryption, the update service — while you fill in your details, style your card in the browser, and tap “Add to Apple Wallet.” Done.'
          }
        ]
      }
    ],
    steps: {
      title: { ar: 'ثلاث خطوات للمحفظة', en: 'Three steps to the Wallet' },
      items: [
        { t: { ar: 'سجّل وعبّي بياناتك', en: 'Sign up & fill your details' }, d: { ar: 'بالعربي والإنجليزي — مع معاينة حيّة.', en: 'Arabic and English — with a live preview.' } },
        { t: { ar: 'اضغط «أضف إلى Wallet»', en: 'Tap “Add to Wallet”' }, d: { ar: 'الباس يتولّد موقّع وجاهز فوراً.', en: 'The pass is generated, signed, instantly.' } },
        { t: { ar: 'شارك من شاشة القفل', en: 'Share from the lock screen' }, d: { ar: 'دبل-كليك، يمسحون الـ QR، انحفظت بياناتك.', en: 'Double-click, they scan the QR, contact saved.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل بطاقة Apple Wallet مجانية؟', en: 'Is the Apple Wallet card free?' },
        a: { ar: 'نعم — إصدار الباس ومشاركته بالـ QR ضمن الخطة المجانية للأبد، بدون بطاقة ائتمانية.', en: 'Yes — issuing the pass and sharing it via QR is part of the free-forever plan, no credit card needed.' }
      },
      {
        q: { ar: 'هل تختفي البطاقة لو حدّثت الآيفون أو غيّرت جوالي؟', en: 'Does the card disappear if I update iOS or change phones?' },
        a: { ar: 'لا — باسات Wallet تنتقل مع نسختك الاحتياطية مثل بقية الباسات. وفي كل الأحوال تقدر تعيد إضافتها من لوحة تحكمك في أي لحظة.', en: 'No — Wallet passes migrate with your backup like any other pass. Either way, you can re-add it from your dashboard at any moment.' }
      },
      {
        q: { ar: 'كيف يتحدّث الباس تلقائياً عند الآخرين؟', en: 'How does the pass auto-update for others?' },
        a: { ar: 'باسات Apple Wallet مربوطة بخدمة تحديث رسمية: لما تعدّل بياناتك، CardPass يرسل إشعار تحديث صامت لكل نسخة من بطاقتك، فتتجدد محتوياتها تلقائياً.', en: 'Apple Wallet passes connect to an official update service: when you edit your details, CardPass sends a silent push to every copy of your card, and its contents refresh automatically.' }
      },
      {
        q: { ar: 'أقدر أتحكم وش يظهر في الباس؟', en: 'Can I control what appears on the pass?' },
        a: { ar: 'نعم — الاسم والمسمى بالعربي أو الإنجليزي، الشركة، الألوان، الشعار، صورتك، ونوع الـ QR (حفظ مباشر أو رابط صفحتك). كل شي من لوحة تحكم بسيطة.', en: 'Yes — name and title in Arabic or English, company, colors, logo, your photo, and the QR mode (instant save or your page link). All from a simple dashboard.' }
      }
    ],
    cta: {
      title: { ar: 'بطاقتك تستاهل مكان بجانب بطاقة الطيران.', en: 'Your card deserves a spot next to your boarding pass.' },
      sub: { ar: 'أنشئها الآن — مجاناً وخلال 30 ثانية.', en: 'Create it now — free, in 30 seconds.' },
      btn: { ar: 'أضف بطاقتك إلى Wallet', en: 'Add your card to Wallet' }
    },
    related: ['digital-business-card', 'qr-code-business-card', 'nfc-business-card']
  },

  'qr-code-business-card': {
    slug: 'qr-code-business-card',
    nav: { ar: 'بطاقة QR', en: 'QR business card' },
    label: { ar: 'مسحة واحدة', en: 'One scan' },
    h1: { ar: 'بطاقة عمل بكود QR — بياناتك تنحفظ بمسحة واحدة', en: 'A QR-code business card — your details saved in one scan' },
    lead: {
      ar: 'أقوى ميزة في البطاقة الرقمية هي لحظة التسليم: الطرف الآخر يرفع كاميرته على كود QR، والجوال يتعرف على بياناتك كجهة اتصال جاهزة للحفظ — اسم، رقم، إيميل، شركة — بدون كتابة حرف واحد. كذا تشتغل بطاقة CardPass.',
      en: 'The most powerful moment of a digital card is the handoff: the other person points their camera at a QR code, and their phone recognizes your details as a ready-to-save contact — name, number, email, company — without typing a single letter. That is how a CardPass works.'
    },
    sections: [
      {
        h2: { ar: 'مو مجرد رابط — جهة اتصال كاملة', en: 'Not just a link — a complete contact' },
        paras: [
          {
            ar: 'فيه فرق جوهري بين كود QR يفتح موقع، وكود QR يحفظ جهة اتصال. الأول يحتاج إنترنت وخطوات إضافية، والثاني — بصيغة vCard القياسية — يخلي iOS وأندرويد يعرضون «إضافة جهة اتصال» فوراً، حتى بدون تغطية شبكة. CardPass يدعم الوضعين وتختار بينهما بضغطة: حفظ مباشر للمؤتمرات والمعارض، أو رابط صفحتك العامة لما تبغى تعرض كل روابطك وأعمالك.',
            en: 'There is a fundamental difference between a QR that opens a website and a QR that saves a contact. The first needs internet and extra steps; the second — using the standard vCard format — makes iOS and Android show “Add contact” immediately, even with no signal. CardPass supports both modes and you switch with one tap: instant-save for conferences and expos, or your public page link when you want to show all your links and work.'
          }
        ]
      },
      {
        h2: { ar: 'وين تحط كود بطاقتك؟', en: 'Where do you put your card’s QR?' },
        bullets: [
          { ar: 'على شاشة جوالك: الباس في Apple Wallet يعرض الـ QR من شاشة القفل.', en: 'On your phone screen: the Apple Wallet pass shows the QR from the lock screen.' },
          { ar: 'في عرضك التقديمي: آخر شريحة فيها الكود — كل الحضور يحفظون بياناتك بثواني.', en: 'In your slide deck: a final slide with the code — the whole room saves your details in seconds.' },
          { ar: 'في توقيع إيميلك وحساباتك: رابط صفحتك العامة يوصل لكل شي.', en: 'In your email signature and bios: your public page link leads to everything.' },
          { ar: 'مطبوعاً عند الحاجة: على بانر معرض أو حتى خلف بطاقة ورقية انتقالية — الكود ما يتغير حتى لو تغيّرت بياناتك.', en: 'Printed when needed: on an expo banner or even the back of a transitional paper card — the code never changes even when your details do.' }
        ],
        paras: [
          {
            ar: 'النقطة الأخيرة هي السر: كود QR الخاص بك ثابت، وبياناته تتبع لوحة تحكمك. غيّر رقمك بعد سنة — نفس الكود المطبوع على البانر يعطي الناس بياناتك الجديدة.',
            en: 'That last point is the secret: your QR code is permanent, and its data follows your dashboard. Change your number a year later — the same code printed on the banner gives people your new details.'
          }
        ]
      },
      {
        h2: { ar: 'وعكسها: امسح أنت بطاقات الناس الورقية', en: 'And the reverse: scan other people’s paper cards' },
        paras: [
          {
            ar: 'التبادل طرفين: أنت تعطي بياناتك بـ QR، لكن الطرف الآخر قد يمد لك كرت ورقي. ميزة المسح في CardPass تصوّر البطاقة الورقية وتستخرج الاسم والرقم والإيميل والشركة وتحوّلها جهة اتصال جاهزة للحفظ خلال ثوانٍ — فتروح من الفعالية وكل العلاقات الجديدة محفوظة، مو كومة كروت في الدرج.',
            en: 'Exchanges go both ways: you share via QR, but the other person may hand you paper. CardPass’s scanner photographs the paper card, extracts the name, number, email, and company, and turns it into a ready-to-save contact within seconds — so you leave the event with every new relationship saved, not a pile of cards in a drawer.'
          }
        ]
      }
    ],
    faqs: [
      {
        q: { ar: 'هل يحتاج الطرف الآخر تطبيق لمسح الكود؟', en: 'Does the receiver need an app to scan the code?' },
        a: { ar: 'لا — كاميرا الجوال العادية تكفي. iOS وأندرويد يتعرفون على vCard مباشرة ويعرضون خيار إضافة جهة الاتصال.', en: 'No — the standard phone camera is enough. iOS and Android recognize vCards natively and offer to add the contact.' }
      },
      {
        q: { ar: 'وش الفرق بين QR حفظ جهة الاتصال وQR رابط الصفحة؟', en: 'What is the difference between a save-contact QR and a page-link QR?' },
        a: { ar: 'حفظ جهة الاتصال يدخل بياناتك في جهات اتصال الماسح فوراً وبدون إنترنت — مثالي للمقابلات السريعة. رابط الصفحة يفتح صفحتك العامة بكل روابطك وأزرار التواصل — مثالي لما تبغى تعطي صورة أكمل. تبدّل بينهما من لوحة التحكم.', en: 'Save-contact puts your details straight into the scanner’s contacts, even offline — ideal for quick encounters. The page link opens your public page with all your links and action buttons — ideal for a fuller picture. Switch between them from the dashboard.' }
      },
      {
        q: { ar: 'هل أعرف كم شخص مسح بطاقتي؟', en: 'Can I see how many people scanned my card?' },
        a: { ar: 'نعم — التحليلات تسجّل كل مسح، فتعرف أي فعالية أو اجتماع ولّد أكثر اهتمام. (ضمن المزايا المتقدمة).', en: 'Yes — analytics log every scan, so you know which event or meeting generated the most interest. (Part of the advanced features.)' }
      },
      {
        q: { ar: 'أقدر أطبع الكود على بطاقتي الورقية الحالية؟', en: 'Can I print the code on my current paper card?' },
        a: { ar: 'أكيد — وهي طريقة انتقالية ذكية: كرتك الورقي يصير بوابة لبطاقتك الرقمية، وبياناتك تظل قابلة للتحديث حتى بعد الطباعة.', en: 'Absolutely — a smart transition: your paper card becomes a gateway to your digital one, and your details stay updatable even after printing.' }
      }
    ],
    cta: {
      title: { ar: 'خلّ بياناتك تنحفظ — مو تنرمى.', en: 'Make your details get saved — not thrown away.' },
      sub: { ar: 'بطاقة QR ذكية في Apple Wallet، مجاناً.', en: 'A smart QR card in Apple Wallet, free.' },
      btn: { ar: 'أنشئ بطاقة QR الآن', en: 'Create your QR card now' }
    },
    related: ['digital-business-card', 'apple-wallet-business-card', 'nfc-business-card']
  },
  'meeting-booking': {
    slug: 'meeting-booking',
    nav: { ar: 'حجز الاجتماعات', en: 'Meeting booking' },
    label: { ar: 'من بطاقتك', en: 'From your card' },
    h1: { ar: 'حجز الاجتماعات من بطاقة عملك الرقمية', en: 'Meeting booking from your digital business card' },
    lead: {
      ar: 'بدل رسائل «متى يناسبك؟» اللي تروح وترجع، خلّ العميل يحجز اجتماعه معك من بطاقتك مباشرة. يفتح بطاقتك، يضغط زر الحجز، يشوف أوقاتك الفاضية بس، ويختار. الاجتماع ينزل في تقويمك، ويوصل إيميل التأكيد لك وله. وحجز الاجتماعات مجاني حاليًا للأفراد ضمن الإطلاق التجريبي.',
      en: 'Instead of the back-and-forth “when suits you?” messages, let clients book a meeting with you straight from your card. They open your card, tap Book, see only your free times, and pick one. The meeting lands in your calendar and a confirmation email reaches both of you. Meeting booking is currently free for individuals during the launch beta.'
    },
    sections: [
      {
        h2: { ar: 'كيف يشتغل الحجز من البطاقة؟', en: 'How does booking from your card work?' },
        paras: [
          {
            ar: 'تربط تقويمك من Google أو Outlook مرة وحدة، وتحدد أيامك وساعاتك ومدة كل اجتماع. بعدها يظهر زر «احجز اجتماعًا» في صفحة بطاقتك. اللي يفتح البطاقة يشوف الأوقات الفاضية فقط، ومواعيد تقويمك الحالية ما تتعارض مع الحجز.',
            en: 'You connect your Google or Outlook calendar once and set your days, hours and meeting lengths. A “Book a meeting” button then appears on your card page. Whoever opens the card sees only your free times, and your existing calendar events never clash with a booking.'
          }
        ],
        bullets: [
          { ar: 'رابط الاجتماع يضاف تلقائيًا: Google Meet مع تقويم Google، وTeams مع ⁦Microsoft 365⁩.', en: 'The meeting link is added for you: Google Meet with Google Calendar, Teams with Microsoft 365.' },
          { ar: 'إيميل تأكيد يوصل لك وللزائر، والزائر يقدر يغيّر الموعد أو يلغيه بنفسه.', en: 'A confirmation email reaches you and the visitor, who can reschedule or cancel on their own.' },
          { ar: 'تقدر تخفي الحجز من صفحة بطاقتك أو ترجّعه متى ما احتجت.', en: 'You can hide booking from your card page or bring it back whenever you need.' }
        ]
      },
      {
        h2: { ar: 'للشركات: رابط حجز واحد للفريق كله', en: 'For companies: one booking link for the whole team' },
        paras: [
          {
            ar: 'مع CardPass للشركات، تقويمات الفريق تنربط، والعميل يحجز من بطاقة الموظف أو من صفحة حجز للفريق، مثل فريق المبيعات أو الدعم. الاجتماع يتوزع بالتناوب أو يروح لأول موظف فاضي، وتحدد الشركة قوالب الاجتماعات ومدتها لكل قسم، وكل موظف يشوف مواعيده من بوابته.',
            en: 'With CardPass for Business, team calendars are connected and clients book from an employee’s card or from a team booking page, such as sales or support. Each meeting goes round robin or to the first one free, the company sets meeting templates and lengths for each team, and each employee sees their appointments in their portal.'
          }
        ]
      },
      {
        h2: { ar: 'خصوصية تقويمك', en: 'Your calendar’s privacy' },
        paras: [
          {
            ar: 'CardPass يستخدم أوقات الانشغال فقط عشان يعرف متى أنت فاضي، وما يحفظ عناوين اجتماعاتك ولا أسماء الحضور. تقدر تفصل التقويم متى ما بغيت، والتفاصيل كاملة في صفحة ربط التقويم.',
            en: 'CardPass uses only your busy times to know when you are free, and never stores your meeting titles or attendees. You can disconnect your calendar at any time; the full details are on the calendar connection page.'
          }
        ]
      }
    ],
    steps: {
      title: { ar: 'فعّل الحجز في ثلاث خطوات', en: 'Turn on booking in three steps' },
      items: [
        { t: { ar: 'اربط تقويمك', en: 'Connect your calendar' }, d: { ar: 'Google أو Outlook، من لوحة التحكم.', en: 'Google or Outlook, from your dashboard.' } },
        { t: { ar: 'حدد أوقاتك', en: 'Set your times' }, d: { ar: 'الأيام والساعات ومدة كل اجتماع.', en: 'Your days, hours and meeting lengths.' } },
        { t: { ar: 'شارك بطاقتك', en: 'Share your card' }, d: { ar: 'زر «احجز اجتماعًا» يظهر في صفحة بطاقتك.', en: 'A “Book a meeting” button shows on your card page.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل حجز الاجتماعات مجاني؟', en: 'Is meeting booking free?' },
        a: { ar: 'نعم، حجز الاجتماعات مجاني حاليًا للأفراد ضمن الإطلاق التجريبي.', en: 'Yes, meeting booking is currently free for individuals during the launch beta.' }
      },
      {
        q: { ar: 'أي تقويم أقدر أربط؟', en: 'Which calendars can I connect?' },
        a: { ar: 'تقويم Google أو Outlook. وللشركات، الربط المركزي يكون عن طريق ⁦Microsoft 365⁩، و⁦Google Workspace⁩ قريبًا.', en: 'Google Calendar or Outlook. For companies, the central connection is through Microsoft 365, with Google Workspace coming soon.' }
      },
      {
        q: { ar: 'هل يحتاج العميل حساب أو تطبيق عشان يحجز؟', en: 'Does the client need an account or an app to book?' },
        a: { ar: 'لا. يفتح بطاقتك من المتصفح، يختار الوقت ويكتب اسمه وإيميله، ويوصله التأكيد على الإيميل.', en: 'No. They open your card in the browser, pick a time and enter their name and email, and the confirmation arrives by email.' }
      },
      {
        q: { ar: 'وش يصير لو تغيّر عندي موعد في التقويم؟', en: 'What if something new lands in my calendar?' },
        a: { ar: 'الأوقات اللي صارت مشغولة في تقويمك تختفي من الحجز، فما ينحجز عليك وقت أنت مرتبط فيه.', en: 'Times that become busy in your calendar disappear from booking, so nobody can book a slot you are already committed to.' }
      },
      {
        q: { ar: 'هل الحجز رابط منفصل عن بطاقتي؟', en: 'Is booking a separate link from my card?' },
        a: { ar: 'لا. الحجز جزء من صفحة بطاقتك نفسها: زر «احجز اجتماعًا» يظهر فيها تلقائيًا، وعندك كمان رابط مباشر لصفحة الحجز إذا حبيت تشاركه لحاله.', en: 'No. Booking is part of your card page itself: a “Book a meeting” button shows on it automatically, and you also get a direct booking link if you want to share it on its own.' }
      },
      {
        q: { ar: 'عندي Outlook أو Hotmail شخصي، وش يصير برابط الاجتماع؟', en: 'I use a personal Outlook or Hotmail. What about the meeting link?' },
        a: { ar: 'الحسابات الشخصية ما تعطي رابط Teams تلقائي، فتحط رابط اجتماعك الثابت مرة وحدة في نوع الاجتماع، ويوصل مع كل حجز.', en: 'Personal accounts can’t get an automatic Teams link, so you set your fixed meeting link once on the meeting type and it goes with every booking.' }
      },
      {
        q: { ar: 'كيف يشتغل الحجز للشركات؟', en: 'How does booking work for companies?' },
        a: { ar: 'مسؤول Microsoft 365 في الشركة يوافق مرة وحدة، وتنربط تقويمات المجموعات اللي تختارها. الشركة تحدد قوالب الاجتماعات، والعميل يحجز من بطاقة الموظف أو من صفحة حجز الفريق، وكل موظف يشوف مواعيده في بوابته. ربط Google Workspace للشركات قريبًا.', en: 'Your company’s Microsoft 365 admin approves once and the calendars of the groups you pick are connected. The company sets meeting templates, clients book from an employee’s card or a team booking page, and each employee sees their meetings in their portal. Google Workspace for companies is coming soon.' }
      },
      {
        q: { ar: 'أقدر أوقف الحجز مؤقتًا؟', en: 'Can I pause booking?' },
        a: { ar: 'إيه، تخفي زر الحجز من صفحة بطاقتك وترجّعه متى ما بغيت. والموظف يقدر يوقف استقبال الحجوزات من بوابته.', en: 'Yes, hide the Book button from your card page and bring it back whenever you like. Employees can pause bookings from their portal.' }
      }
    ],
    cta: {
      title: { ar: 'خلّ عملاءك يحجزون بدون رسائل.', en: 'Let clients book without the back-and-forth.' },
      sub: { ar: 'حجز الاجتماعات مجاني حاليًا للأفراد ضمن الإطلاق التجريبي.', en: 'Meeting booking is currently free for individuals during the launch beta.' },
      btn: { ar: 'فعّل الحجز في بطاقتك', en: 'Turn on booking for your card' }
    },
    related: ['digital-business-card', 'apple-wallet-business-card', 'qr-code-business-card']
  }
  ,

  'electronic-business-card': {
    slug: 'electronic-business-card',
    nav: { ar: 'كرت شخصي إلكتروني', en: 'Electronic business card' },
    label: { ar: 'بديل الكرت الورقي', en: 'The paper card replacement' },
    h1: { ar: 'كرت شخصي إلكتروني — بطاقة أعمال إلكترونية في جوالك', en: 'An electronic business card that lives on your phone' },
    lead: {
      ar: 'تدوّر على كرت شخصي إلكتروني أو «بزنس كارد» بدل الكروت المطبوعة؟ CardPass يعطيك بطاقة أعمال إلكترونية تنضاف في Apple Wallet و Google Wallet، يمسحها الطرف الآخر بالكاميرا فتنحفظ بياناتك عنده مباشرة. مجانًا، وخلال 30 ثانية.',
      en: 'Looking for an electronic business card instead of printed ones? CardPass gives you an electronic business card that goes into Apple Wallet and Google Wallet; the other person scans it with their camera and your details are saved straight away. Free, in 30 seconds.'
    },
    sections: [
      {
        h2: { ar: 'وش هو الكرت الشخصي الإلكتروني؟', en: 'What is an electronic business card?' },
        paras: [
          {
            ar: 'هو نفس كرت العمل اللي تعرفه: اسمك، مسماك، شركتك، جوالك، إيميلك وحساباتك. الفرق إنه ما ينطبع على ورق. يعيش في جوالك، وتشاركه بكود QR أو برابط. بعض الناس يسمّونه بطاقة عمل رقمية، أو بطاقة أعمال إلكترونية، أو بزنس كارد إلكتروني، أو كرت بزنس ذكي. كلها نفس الفكرة.',
            en: 'It is the same business card you know: your name, title, company, phone, email and accounts. The difference is that it is not printed. It lives on your phone and you share it with a QR code or a link. People also call it a digital business card or a smart business card. Same idea.'
          }
        ],
        bullets: [
          { ar: 'ما يخلص ولا تحتاج تطبع دفعة جديدة.', en: 'It never runs out and you never reprint.' },
          { ar: 'لو تغيّر رقمك أو مسماك، تعدّله مرة ويتحدّث عند الكل.', en: 'If your number or title changes, edit once and it updates for everyone.' },
          { ar: 'الطرف الآخر يحفظك جهة اتصال بمسحة، بدون ما يكتب شي.', en: 'The other person saves you as a contact with one scan, no typing.' },
          { ar: 'بالعربي والإنجليزي في نفس البطاقة.', en: 'Arabic and English on the same card.' }
        ]
      },
      {
        h2: { ar: 'الفرق بين الكرت الإلكتروني في CardPass وصورة كرت في الجوال', en: 'CardPass versus a photo of a card on your phone' },
        paras: [
          {
            ar: 'ناس كثير يصوّرون كرتهم الورقي ويرسلونه واتساب. المشكلة إن الصورة ما تنحفظ جهة اتصال، وتضيع بين المحادثات. بطاقة CardPass باس حقيقي في المحفظة: تفتحها من شاشة القفل، ويمسحها العميل فيطلع له زر «حفظ جهة الاتصال» فورًا. وعندك صفحة عامة برابط باسمك تقدر تحطها في توقيع الإيميل أو البايو.',
            en: 'Many people photograph their paper card and send it on WhatsApp. A photo cannot be saved as a contact and gets lost in chats. A CardPass card is a real wallet pass: open it from the lock screen, the client scans it and gets “Save contact” immediately. You also get a public page with your own link for your email signature or bio.'
          }
        ]
      },
      {
        h2: { ar: 'كرت إلكتروني لكل موظفين شركتك', en: 'An electronic card for every employee' },
        paras: [
          {
            ar: 'لو أنت مسؤول في شركة، تقدر تطلع كروت إلكترونية لكل الفريق بنفس هوية الشركة وألوانها، تضيف الموظفين واحد واحد أو من ملف Excel، وكل موظف يضيف بطاقته لمحفظته من بوابته. ولما أحد يطلع من الشركة تحذفه من اللوحة.',
            en: 'If you run a company, issue electronic cards for the whole team in your brand colors, add employees one by one or from an Excel file, and each employee adds their card to their wallet from their portal. When someone leaves, remove them from the dashboard.'
          }
        ]
      }
    ],
    compare: {
      title: { ar: 'الكرت الورقي مقابل الكرت الإلكتروني', en: 'Paper card versus electronic card' },
      colA: { ar: 'كرت ورقي مطبوع', en: 'Printed paper card' },
      colB: { ar: 'كرت CardPass الإلكتروني', en: 'CardPass electronic card' },
      rows: [
        { f: { ar: 'التكلفة', en: 'Cost' }, a: { ar: 'طباعة كل كم شهر', en: 'Reprinting every few months' }, b: { ar: 'مجاني', en: 'Free' } },
        { f: { ar: 'تغيير الرقم أو المسمى', en: 'Changing your number or title' }, a: { ar: 'تطبع من جديد', en: 'Reprint' }, b: { ar: 'تعدّل مرة ويتحدّث عند الكل', en: 'Edit once, updates for everyone' } },
        { f: { ar: 'حفظ البيانات عند العميل', en: 'Saving your details' }, a: { ar: 'يكتبها بيده أو يرمي الكرت', en: 'Typed by hand or thrown away' }, b: { ar: 'مسحة QR وتنحفظ جهة اتصال', en: 'One QR scan saves the contact' } },
        { f: { ar: 'لو خلصت الكروت', en: 'When you run out' }, a: { ar: 'ما عندك شي تعطيه', en: 'Nothing to hand out' }, b: { ar: 'ما تخلص أبدًا', en: 'Never runs out' } },
        { f: { ar: 'اللغة', en: 'Language' }, a: { ar: 'وجه عربي ووجه إنجليزي', en: 'One side per language' }, b: { ar: 'عربي وإنجليزي في صفحة وحدة', en: 'Arabic and English on one page' } }
      ]
    },
    steps: {
      title: { ar: 'سوّ كرتك الإلكتروني في 3 خطوات', en: 'Make your electronic card in 3 steps' },
      items: [
        { t: { ar: 'سجّل مجانًا', en: 'Sign up free' }, d: { ar: 'بالإيميل أو حساب Google.', en: 'With email or a Google account.' } },
        { t: { ar: 'عبّي بياناتك واختر شكلها', en: 'Fill in your details and style' }, d: { ar: 'اسمك ومسماك وألوانك وشعارك وصورتك.', en: 'Name, title, colors, logo and photo.' } },
        { t: { ar: 'أضفها للمحفظة وشاركها', en: 'Add it to your wallet and share' }, d: { ar: 'Apple Wallet أو Google Wallet، والمشاركة بـ QR.', en: 'Apple Wallet or Google Wallet, shared by QR.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'كم سعر الكرت الشخصي الإلكتروني؟', en: 'How much is an electronic business card?' },
        a: { ar: 'البطاقة الأساسية مجانية: باس في المحفظة وكود QR يحفظ جهة الاتصال. ما تحتاج بطاقة ائتمانية.', en: 'The core card is free: a wallet pass and a QR that saves the contact. No credit card needed.' }
      },
      {
        q: { ar: 'هل يشتغل مع الأندرويد؟', en: 'Does it work on Android?' },
        a: { ar: 'إيه. صاحب الأندرويد يضيف بطاقته في Google Wallet، وأي جوال آيفون أو أندرويد يقدر يمسح الكود بالكاميرا ويحفظ بياناتك.', en: 'Yes. Android users add their card to Google Wallet, and any iPhone or Android camera can scan the code and save your details.' }
      },
      {
        q: { ar: 'هل العميل يحتاج ينزّل تطبيق؟', en: 'Does the client need an app?' },
        a: { ar: 'لا. يمسح الكود بكاميرا جواله وتنحفظ بياناتك أو تنفتح صفحتك. بدون أي تطبيق.', en: 'No. They scan the code with their camera and your details are saved or your page opens. No app.' }
      },
      {
        q: { ar: 'أقدر أطبع الكود على كرت ورقي أو ستاند؟', en: 'Can I print the code on a paper card or stand?' },
        a: { ar: 'إيه، الكود حقك وتقدر تطبعه في أي مكان، ولأنه يفتح بطاقتك المحدّثة دايم ما تحتاج تغيّر المطبوع لو تغيّرت بياناتك.', en: 'Yes, the code is yours to print anywhere, and since it always opens your latest card you never reprint when your details change.' }
      }
    ],
    cta: {
      title: { ar: 'ودّع الكروت الورقية.', en: 'Say goodbye to paper cards.' },
      sub: { ar: 'سوّ كرتك الشخصي الإلكتروني الحين، مجانًا.', en: 'Make your electronic business card now, free.' },
      btn: { ar: 'سوّ كرتك مجانًا', en: 'Make your card free' }
    },
    related: ['digital-business-card', 'add-card-to-apple-wallet', 'free-business-card-maker']
  },

  'add-card-to-apple-wallet': {
    slug: 'add-card-to-apple-wallet',
    nav: { ar: 'إضافة بطاقة للمحفظة', en: 'Add a card to Wallet' },
    label: { ar: 'دليل خطوة بخطوة', en: 'Step-by-step guide' },
    h1: { ar: 'كيف أضيف بطاقة عمل في محفظة الآيفون (Apple Wallet)؟', en: 'How do I add a business card to Apple Wallet on iPhone?' },
    lead: {
      ar: 'محفظة الآيفون ما فيها زر «أضف بطاقة عمل» جاهز، لأنها تقبل بس الباسات اللي تصدرها جهة موقّعة من Apple. هنا نشرح لك الطريقة الصحيحة خطوة بخطوة، وكيف تضيفها في Google Wallet لو جوالك أندرويد.',
      en: 'Apple Wallet has no built-in “add business card” button, because it only accepts passes issued by an Apple-signed issuer. Here is the right way, step by step, plus how to add it to Google Wallet on Android.'
    },
    sections: [
      {
        h2: { ar: 'ليش ما أقدر أضيف صورة كرتي في المحفظة؟', en: 'Why can’t I add a photo of my card to Wallet?' },
        paras: [
          {
            ar: 'المحفظة تقبل نوع خاص من الملفات اسمه «باس»، ولازم يكون موقّع بشهادة من Apple. صورة الكرت أو ملف PDF ما ينضافون. عشان كذا تحتاج خدمة تصدر لك باس موقّع، وهذا اللي يسويه CardPass: يجهّز الباس ويوقّعه ويربطه بخدمة التحديث، وأنت بس تضغط «إضافة».',
            en: 'Wallet accepts a special pass file (.pkpass) signed with an Apple certificate. A photo or a PDF cannot be added. You need a service that issues a signed pass, which is what CardPass does: it builds and signs the pass and links it to the update service. You just tap “Add”.'
          }
        ]
      },
      {
        h2: { ar: 'الطريقة على الآيفون', en: 'On iPhone' },
        paras: [
          {
            ar: 'افتح cardpass.co من Safari وسجّل، عبّي بياناتك، ثم اضغط زر «أضف إلى Apple Wallet». يطلع لك شكل البطاقة، اضغط «إضافة» فوق يمين الشاشة. خلاص، صارت في المحفظة. تقدر بعدها تفتحها بدبل كليك على الزر الجانبي، وتوريها للعميل يمسح الكود.',
            en: 'Open cardpass.co in Safari and sign up, fill in your details, then tap “Add to Apple Wallet”. The card preview appears; tap “Add” at the top right. Done. Open it later by double-clicking the side button and let the client scan the code.'
          }
        ],
        bullets: [
          { ar: 'تقدر كمان تستخدم تطبيق CardPass من App Store.', en: 'You can also use the CardPass app from the App Store.' },
          { ar: 'لو أضفتها من الكمبيوتر، تنرسل لجوالك عن طريق الكود أو الإيميل.', en: 'If you start on a computer, send it to your phone by QR or email.' }
        ]
      },
      {
        h2: { ar: 'الطريقة على الأندرويد (Google Wallet)', en: 'On Android (Google Wallet)' },
        paras: [
          {
            ar: 'نفس الخطوات من متصفح Chrome، بس اضغط «أضف إلى Google Wallet» بدل Apple Wallet. البطاقة تنحفظ في محفظة Google وتطلعها من التطبيق متى ما احتجتها.',
            en: 'Same steps in Chrome, but tap “Add to Google Wallet” instead. The card is saved in Google Wallet and you open it from the app whenever you need it.'
          }
        ]
      },
      {
        h2: { ar: 'إذا ما طلع زر الإضافة', en: 'If the add button doesn’t appear' },
        bullets: [
          { ar: 'افتح الرابط من Safari مو من متصفح داخل تطبيق ثاني (مثل إنستقرام).', en: 'Open the link in Safari, not an in-app browser (like Instagram).' },
          { ar: 'تأكد إن تطبيق المحفظة موجود في جوالك.', en: 'Make sure the Wallet app is installed.' },
          { ar: 'لو تعدّل بياناتك بعدين، ما تحتاج تضيفها من جديد. تتحدّث لحالها.', en: 'If you edit later you don’t re-add it. It updates by itself.' }
        ],
        paras: []
      }
    ],
    steps: {
      title: { ar: 'الخلاصة في 3 خطوات', en: 'In 3 steps' },
      items: [
        { t: { ar: 'سجّل في CardPass', en: 'Sign up to CardPass' }, d: { ar: 'من Safari أو Chrome، مجانًا.', en: 'From Safari or Chrome, free.' } },
        { t: { ar: 'عبّي بياناتك', en: 'Fill in your details' }, d: { ar: 'مع معاينة حيّة للبطاقة.', en: 'With a live card preview.' } },
        { t: { ar: 'اضغط «أضف إلى Wallet»', en: 'Tap “Add to Wallet”' }, d: { ar: 'ثم «إضافة»، وصارت في محفظتك.', en: 'Then “Add”, and it is in your wallet.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل إضافة بطاقة العمل للمحفظة مجانية؟', en: 'Is adding a business card to Wallet free?' },
        a: { ar: 'إيه، إصدار البطاقة وإضافتها للمحفظة ومشاركتها بالكود كلها مجانية.', en: 'Yes, issuing the card, adding it to Wallet and sharing it by code are all free.' }
      },
      {
        q: { ar: 'هل تشتغل البطاقة بدون إنترنت؟', en: 'Does the card work offline?' },
        a: { ar: 'إيه، البطاقة محفوظة في جوالك وتفتح بدون نت، والكود يحفظ جهة الاتصال عند الطرف الآخر حتى لو ما عنده نت.', en: 'Yes, the card is stored on your phone and opens offline, and the code saves the contact on the other phone even without internet.' }
      },
      {
        q: { ar: 'أقدر أضيف أكثر من بطاقة؟', en: 'Can I add more than one card?' },
        a: { ar: 'كل حساب له بطاقته. ولو شركتك تستخدم CardPass للشركات، تجيك بطاقة الشركة من بوابة الموظف.', en: 'Each account has its card. If your company uses CardPass for Business, your company card comes from the employee portal.' }
      },
      {
        q: { ar: 'كيف أحذف البطاقة من المحفظة؟', en: 'How do I remove the card from Wallet?' },
        a: { ar: 'افتح البطاقة في المحفظة، اضغط زر النقاط الثلاث، واختر «إزالة البطاقة».', en: 'Open the card in Wallet, tap the three-dots button, and choose “Remove Pass”.' }
      }
    ],
    cta: {
      title: { ar: 'بطاقتك في المحفظة خلال 30 ثانية.', en: 'Your card in Wallet in 30 seconds.' },
      sub: { ar: 'جرّبها الحين، مجانًا.', en: 'Try it now, free.' },
      btn: { ar: 'أضف بطاقتك للمحفظة', en: 'Add your card to Wallet' }
    },
    related: ['apple-wallet-business-card', 'electronic-business-card', 'qr-code-business-card']
  },

  'free-business-card-maker': {
    slug: 'free-business-card-maker',
    nav: { ar: 'تصميم بطاقة عمل مجانًا', en: 'Free business card maker' },
    label: { ar: 'صمّم أونلاين', en: 'Design online' },
    h1: { ar: 'تصميم بطاقة عمل مجانًا أونلاين — وتكون جاهزة في جوالك', en: 'Design a business card online for free, ready on your phone' },
    lead: {
      ar: 'بدل ما تدوّر على مصمم أو قالب وتنتظر المطبعة، صمّم بطاقة عملك بنفسك من المتصفح: اختر الألوان، حط شعارك وصورتك، وشوف المعاينة وأنت تعدّل. النتيجة بطاقة تعيش في محفظة جوالك وتشاركها بكود QR، مجانًا.',
      en: 'Instead of hunting for a designer or template and waiting for the printer, design your business card yourself in the browser: pick colors, add your logo and photo, and watch a live preview. The result lives in your phone wallet and is shared by QR, free.'
    },
    sections: [
      {
        h2: { ar: 'وش تقدر تخصّص في بطاقتك؟', en: 'What can you customize?' },
        paras: [
          {
            ar: 'كل اللي يهمك في كرت العمل موجود، وتشوفه يتغيّر قدامك لحظة بلحظة:',
            en: 'Everything that matters on a business card is there, and you see it change live:'
          }
        ],
        bullets: [
          { ar: 'الاسم والمسمى الوظيفي والشركة بالعربي والإنجليزي.', en: 'Name, job title and company in Arabic and English.' },
          { ar: 'ألوان البطاقة وشعار الشركة وصورتك الشخصية.', en: 'Card colors, company logo and your photo.' },
          { ar: 'الجوال والإيميل والموقع وحسابات التواصل.', en: 'Phone, email, website and social accounts.' },
          { ar: 'صفحة عامة برابط باسمك، وتقدر تختار شكلها.', en: 'A public page with your own link, in the layout you choose.' }
        ]
      },
      {
        h2: { ar: 'ليش رقمية وليست للطباعة؟', en: 'Why digital instead of print?' },
        paras: [
          {
            ar: 'الكرت المطبوع ينتهي لما تتغيّر بياناتك أو تخلص الكمية. البطاقة الرقمية تتعدّل في أي وقت وتتحدّث عند كل اللي أضافوها، ويحفظك العميل جهة اتصال بمسحة وحدة. ولو تبي نسخة مطبوعة، اطبع الكود على كرتك الورقي، وبيفتح بطاقتك المحدّثة دايم.',
            en: 'A printed card expires when your details change or you run out. A digital card can be edited anytime and updates for everyone who added it, and clients save you with one scan. Want print too? Put the QR on your paper card and it always opens your latest card.'
          }
        ]
      },
      {
        h2: { ar: 'لفريقك كامل بنفس الهوية', en: 'For your whole team, one brand' },
        paras: [
          {
            ar: 'الشركات تحدد الهوية مرة وحدة، وكل موظف تطلع بطاقته بنفس الألوان والشعار. وتقدر تفرّق بين المستويات بألوان مختلفة، وتضيف الموظفين من ملف Excel.',
            en: 'Companies set the brand once and every employee card follows the same colors and logo. Tiers can get different colors, and employees can be imported from Excel.'
          }
        ]
      }
    ],
    steps: {
      title: { ar: 'صمّم بطاقتك الآن', en: 'Design your card now' },
      items: [
        { t: { ar: 'سجّل مجانًا', en: 'Sign up free' }, d: { ar: 'بدون بطاقة ائتمانية.', en: 'No credit card.' } },
        { t: { ar: 'صمّم وشوف المعاينة', en: 'Design with a live preview' }, d: { ar: 'ألوانك وشعارك وصورتك.', en: 'Your colors, logo and photo.' } },
        { t: { ar: 'أضفها للمحفظة', en: 'Add it to your wallet' }, d: { ar: 'وشاركها بكود QR.', en: 'And share it by QR.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل التصميم مجاني فعلًا؟', en: 'Is the design really free?' },
        a: { ar: 'إيه، تسجيلك وتصميم بطاقتك وإضافتها للمحفظة مجاني. وخلال الإطلاق تنفتح لك مزايا إضافية كتجربة.', en: 'Yes, signing up, designing your card and adding it to your wallet are free. During launch extra features open as a trial.' }
      },
      {
        q: { ar: 'أحتاج خبرة في التصميم؟', en: 'Do I need design skills?' },
        a: { ar: 'لا. تختار من الخيارات وتشوف النتيجة مباشرة، والبطاقة تطلع مرتبة تلقائيًا.', en: 'No. Pick from the options and see the result live; the card lays itself out.' }
      },
      {
        q: { ar: 'أقدر أغيّر التصميم بعدين؟', en: 'Can I change the design later?' },
        a: { ar: 'متى ما بغيت، والتغيير يوصل للبطاقة في محفظتك ومحافظ اللي أضافوها.', en: 'Anytime, and the change reaches the card in your wallet and in everyone else’s.' }
      }
    ],
    cta: {
      title: { ar: 'بطاقة بتصميمك، في جوالك، اليوم.', en: 'Your design, on your phone, today.' },
      sub: { ar: 'ابدأ مجانًا خلال 30 ثانية.', en: 'Start free in 30 seconds.' },
      btn: { ar: 'صمّم بطاقتك', en: 'Design your card' }
    },
    related: ['electronic-business-card', 'digital-business-card', 'add-card-to-apple-wallet']
  }
  ,

  'employee-business-cards': {
    slug: 'employee-business-cards',
    nav: { ar: 'بطاقات عمل الموظفين', en: 'Employee business cards' },
    label: { ar: 'للشركات', en: 'For companies' },
    h1: { ar: 'بطاقات عمل رقمية لموظفين شركتك — بهوية وحدة ولوحة وحدة', en: 'Digital business cards for your employees, one brand, one dashboard' },
    lead: {
      ar: 'بدل ما تطبع كروت لكل موظف وتعيد طباعتها مع كل ترقية أو تغيير رقم، أعطِ فريقك بطاقات عمل رقمية بهوية شركتك في Apple Wallet و Google Wallet. تضيف الموظفين من ملف Excel، وتعدّل من لوحة وحدة، والبطاقة تتحدّث في جوال الموظف لحالها.',
      en: 'Instead of printing cards for every employee and reprinting with every promotion or number change, give your team digital business cards in your brand in Apple Wallet and Google Wallet. Import employees from Excel, edit from one dashboard, and the card updates on the employee’s phone by itself.'
    },
    sections: [
      {
        h2: { ar: 'وش تحصل الشركة؟', en: 'What the company gets' },
        paras: [
          {
            ar: 'لوحة تحكم للشركة فيها كل الموظفين وبطاقاتهم. تحدد الهوية (الشعار والألوان) مرة وحدة، وكل بطاقة تطلع مطابقة لها، فما في موظف يسوي كرت بشكل مختلف.',
            en: 'A company dashboard with every employee and their card. Set the brand (logo and colors) once and every card matches it, so nobody makes an off-brand card.'
          }
        ],
        bullets: [
          { ar: 'إضافة الموظفين واحد واحد أو من ملف Excel، وإرسال دعوة لكل واحد.', en: 'Add employees one by one or from Excel, and send each an invite.' },
          { ar: 'فئات بطاقات بألوان مختلفة حسب المستوى أو القسم.', en: 'Card tiers in different colors by level or department.' },
          { ar: 'تشوف مين أضاف بطاقته للمحفظة ومين باقي.', en: 'See who has added their card to their wallet and who hasn’t.' },
          { ar: 'تحدد وش يقدر الموظف يعدّل في بطاقته.', en: 'Decide what each employee can edit on their card.' },
          { ar: 'إشعار يوصل لبطاقات الفريق كلهم بضغطة.', en: 'Send a notice to the whole team’s cards in one tap.' },
          { ar: 'تدير الفريق من المتصفح أو من تطبيق كارد باس على الآيفون.', en: 'Manage the team from the browser or the CardPass iPhone app.' }
        ]
      },
      {
        h2: { ar: 'وش يحصل الموظف؟', en: 'What the employee gets' },
        paras: [
          {
            ar: 'بوابة خاصة فيها بطاقته، يضيفها لمحفظة جواله بضغطة. يفتحها من شاشة القفل في أي اجتماع أو معرض، والعميل يمسح الكود فينحفظ الموظف عنده جهة اتصال باسمه ومسماه وشعار الشركة.',
            en: 'A personal portal with their card, added to their phone wallet in one tap. They open it from the lock screen at any meeting or exhibition, the client scans the code, and the employee is saved as a contact with name, title and company logo.'
          }
        ]
      },
      {
        h2: { ar: 'لما يتغيّر شي أو يطلع موظف', en: 'When something changes or someone leaves' },
        paras: [
          {
            ar: 'ترقية، رقم جديد، فرع جديد: تعدّل من اللوحة والبطاقة تتحدّث في جوال الموظف وعند العملاء اللي أضافوها. ولما يطلع موظف من الشركة تحذفه من اللوحة، بدون كروت مطبوعة تبقى متداولة باسم الشركة.',
            en: 'A promotion, a new number, a new branch: edit in the dashboard and the card updates on the employee’s phone and for clients who added it. When someone leaves, remove them from the dashboard, with no printed cards still circulating in the company’s name.'
          }
        ]
      },
      {
        h2: { ar: 'وحجز اجتماعات مع فريقك', en: 'And meeting booking with your team' },
        paras: [
          {
            ar: 'تقدر تربط تقويمات Microsoft 365 لفريقك، فيحجز العميل اجتماع مع الموظف من بطاقته مباشرة، والاجتماع ينزل في تقويمه برابط Teams. ربط Google Workspace للشركات قريبًا.',
            en: 'Connect your team’s Microsoft 365 calendars and clients book a meeting with an employee straight from their card, landing in their calendar with a Teams link. Google Workspace for companies is coming soon.'
          }
        ]
      }
    ],
    steps: {
      title: { ar: 'من الورق للرقمي خلال أسبوع', en: 'From paper to digital within a week' },
      items: [
        { t: { ar: 'سجّل شركتك', en: 'Register your company' }, d: { ar: 'واحجز ديمو مع فريق كارد باس.', en: 'And book a demo with the CardPass team.' } },
        { t: { ar: 'ارفع هويتك وأضف الفريق', en: 'Upload your brand and add the team' }, d: { ar: 'الشعار والألوان، والموظفين من Excel.', en: 'Logo and colors, employees from Excel.' } },
        { t: { ar: 'الموظفين يفعّلون بطاقاتهم', en: 'Employees activate their cards' }, d: { ar: 'كل واحد يضيف بطاقته لمحفظته.', en: 'Each adds their card to their wallet.' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'كم يكلّف للشركة؟', en: 'What does it cost a company?' },
        a: { ar: 'السعر حسب عدد الموظفين ومدة الاشتراك. احجز ديمو ونرسل لك عرض سعر يناسب فريقك.', en: 'Pricing depends on team size and term. Book a demo and we send a quote for your team.' }
      },
      {
        q: { ar: 'هل الموظف يحتاج آيفون؟', en: 'Do employees need an iPhone?' },
        a: { ar: 'لا. الآيفون يضيفها في Apple Wallet، والأندرويد في Google Wallet. والعميل يمسح الكود بأي جوال.', en: 'No. iPhone users add it to Apple Wallet, Android users to Google Wallet, and clients scan with any phone.' }
      },
      {
        q: { ar: 'نقدر نستورد الموظفين من نظامنا؟', en: 'Can we import employees from our system?' },
        a: { ar: 'إيه، صدّر قائمة الموظفين من نظامكم كملف Excel وارفعه في اللوحة.', en: 'Yes, export your employee list as an Excel file and upload it in the dashboard.' }
      },
      {
        q: { ar: 'هل البطاقة بالعربي والإنجليزي؟', en: 'Is the card in Arabic and English?' },
        a: { ar: 'إيه، الاسم والمسمى بالعربي والإنجليزي، والصفحة العامة للموظف باللغتين.', en: 'Yes, name and title in Arabic and English, and the employee’s public page in both.' }
      }
    ],
    cta: {
      title: { ar: 'جاهزين تنقلون فريقكم للرقمي؟', en: 'Ready to move your team to digital?' },
      sub: { ar: 'احجز ديمو ونوريك اللوحة على بيانات شركتك.', en: 'Book a demo and see the dashboard with your company.' },
      btn: { ar: 'احجز ديمو', en: 'Book a demo' },
      to: '/business#book-demo'
    },
    related: ['business-card-printing-alternative', 'sales-team-business-cards', 'digital-business-card']
  },

  'business-card-printing-alternative': {
    slug: 'business-card-printing-alternative',
    nav: { ar: 'بديل طباعة كروت الشركة', en: 'Card printing alternative' },
    label: { ar: 'للشركات', en: 'For companies' },
    h1: { ar: 'بديل طباعة الكروت الشخصية لموظفين الشركة', en: 'An alternative to printing business cards for your staff' },
    lead: {
      ar: 'طباعة كروت الموظفين تبان مصروف بسيط، لكنها تتكرر: موظف جديد، ترقية، رقم تغيّر، شعار تحدّث، والكروت القديمة ترمى. البطاقات الرقمية تنهي هذي الدورة: بطاقة وحدة لكل موظف تتعدّل من اللوحة ولا تنطبع أبدًا.',
      en: 'Printing staff cards looks like a small expense, but it repeats: a new hire, a promotion, a changed number, a refreshed logo, and the old cards get binned. Digital cards end that cycle: one card per employee, edited from the dashboard, never printed.'
    },
    sections: [
      {
        h2: { ar: 'وين تروح تكلفة الكروت المطبوعة؟', en: 'Where printed card costs go' },
        bullets: [
          { ar: 'كل موظف جديد يحتاج تصميم وطباعة وانتظار.', en: 'Every new hire needs design, printing and waiting.' },
          { ar: 'كل ترقية أو تغيير رقم يعني كروت جديدة والقديمة ترمى.', en: 'Every promotion or number change means new cards and wasted old ones.' },
          { ar: 'لما يتغيّر الشعار أو العنوان، تعيد طباعة الشركة كلها.', en: 'When the logo or address changes, the whole company reprints.' },
          { ar: 'الموظف اللي يطلع تبقى كروته عند العملاء باسم الشركة.', en: 'Cards of people who left stay with clients in the company’s name.' },
          { ar: 'وأغلب الكروت ما تنحفظ أصلًا، تنرمى بعد الاجتماع.', en: 'And most cards are never saved; they’re thrown away after the meeting.' }
        ],
        paras: []
      },
      {
        h2: { ar: 'كيف تحلها البطاقات الرقمية؟', en: 'How digital cards solve it' },
        paras: [
          {
            ar: 'كل موظف عنده بطاقة في محفظة جواله بهوية الشركة. التعديل من اللوحة يوصل لجواله ولكل عميل أضافها، والعميل يحفظ الموظف جهة اتصال بمسحة بدل ما يرمي الكرت. ولما يطلع الموظف تحذفه من اللوحة.',
            en: 'Every employee has a card in their phone wallet in the company’s brand. Edits in the dashboard reach their phone and every client who added it, and clients save the employee as a contact with one scan instead of binning a card. When someone leaves, remove them from the dashboard.'
          }
        ]
      },
      {
        h2: { ar: 'وإذا نحتاج شي مطبوع؟', en: 'What if we still need print?' },
        paras: [
          {
            ar: 'تقدر تطبع كود البطاقة على ستاند في الاستقبال أو المعرض، أو على ظهر كرت موحّد بدون بيانات تتغيّر. الكود يفتح البطاقة المحدّثة دايم.',
            en: 'Print the card’s code on a reception or exhibition stand, or on the back of a generic card with no changing details. The code always opens the latest card.'
          }
        ]
      }
    ],
    compare: {
      title: { ar: 'الطباعة مقابل كارد باس', en: 'Printing versus CardPass' },
      colA: { ar: 'كروت مطبوعة للموظفين', en: 'Printed staff cards' },
      colB: { ar: 'بطاقات كارد باس الرقمية', en: 'CardPass digital cards' },
      rows: [
        { f: { ar: 'موظف جديد', en: 'New hire' }, a: { ar: 'تصميم وطباعة وانتظار', en: 'Design, print, wait' }, b: { ar: 'تضيفه من اللوحة وتوصله دعوة', en: 'Add in the dashboard, invite sent' } },
        { f: { ar: 'ترقية أو رقم جديد', en: 'Promotion or new number' }, a: { ar: 'طباعة من جديد', en: 'Reprint' }, b: { ar: 'تعديل يتحدّث لحاله', en: 'Edit, updates by itself' } },
        { f: { ar: 'تحديث الهوية', en: 'Brand refresh' }, a: { ar: 'إعادة طباعة للشركة كلها', en: 'Whole-company reprint' }, b: { ar: 'تغيّر الشعار والألوان مرة', en: 'Change logo and colors once' } },
        { f: { ar: 'موظف يطلع', en: 'Employee leaves' }, a: { ar: 'كروته تبقى متداولة', en: 'Cards keep circulating' }, b: { ar: 'تحذفه من اللوحة', en: 'Remove from dashboard' } },
        { f: { ar: 'عند العميل', en: 'At the client' }, a: { ar: 'كرت ينرمى غالبًا', en: 'Usually binned' }, b: { ar: 'جهة اتصال محفوظة بمسحة', en: 'Contact saved with one scan' } }
      ]
    },
    faqs: [
      {
        q: { ar: 'هل نقدر نبدأ بقسم واحد؟', en: 'Can we start with one department?' },
        a: { ar: 'إيه، كثير شركات تبدأ بفريق المبيعات أو الإدارة، وتضيف الباقي بعدين.', en: 'Yes, many companies start with sales or management and add the rest later.' }
      },
      {
        q: { ar: 'كم ياخذ التفعيل؟', en: 'How long does rollout take?' },
        a: { ar: 'خلال أسبوع من التوقيع لين أول بطاقة في جوال أول موظف.', en: 'Within a week from signing to the first card on the first employee’s phone.' }
      },
      {
        q: { ar: 'العميل لازم ينزّل تطبيق؟', en: 'Does the client need an app?' },
        a: { ar: 'لا، يمسح الكود بكاميرا جواله وينحفظ الموظف عنده.', en: 'No, they scan the code with their camera and the employee is saved.' }
      }
    ],
    cta: {
      title: { ar: 'آخر دفعة كروت تطبعها.', en: 'The last batch of cards you print.' },
      sub: { ar: 'احجز ديمو ونحسب معك وش توفّر الشركة.', en: 'Book a demo and we work out what your company saves.' },
      btn: { ar: 'احجز ديمو', en: 'Book a demo' },
      to: '/business#book-demo'
    },
    related: ['employee-business-cards', 'sales-team-business-cards', 'nfc-business-card']
  },

  'sales-team-business-cards': {
    slug: 'sales-team-business-cards',
    nav: { ar: 'بطاقات فريق المبيعات', en: 'Sales team cards' },
    label: { ar: 'للشركات', en: 'For companies' },
    h1: { ar: 'بطاقات رقمية لفريق المبيعات — العميل يحفظك ويحجز معك من نفس البطاقة', en: 'Digital cards for sales teams: clients save you and book you from the same card' },
    lead: {
      ar: 'فريق المبيعات يقابل عشرات العملاء في الأسبوع، في المعارض والزيارات والاجتماعات. البطاقة الرقمية تخلي كل مقابلة تنتهي بجهة اتصال محفوظة في جوال العميل، وبزر يحجز فيه الاجتماع الجاي مباشرة في تقويم المندوب.',
      en: 'Sales teams meet dozens of clients a week at exhibitions, visits and meetings. A digital card makes every encounter end with a saved contact on the client’s phone and a button to book the next meeting straight into the rep’s calendar.'
    },
    sections: [
      {
        h2: { ar: 'في المعارض والزيارات', en: 'At exhibitions and visits' },
        paras: [
          {
            ar: 'المندوب يفتح بطاقته من شاشة القفل، العميل يمسح الكود، وينحفظ المندوب عنده باسمه ورقمه وشعار الشركة. ما في كروت تخلص في نص المعرض، ولا عميل يضيّع الكرت في شنطته.',
            en: 'The rep opens their card from the lock screen, the client scans, and the rep is saved with name, number and company logo. No running out of cards mid-exhibition, no card lost in a bag.'
          }
        ]
      },
      {
        h2: { ar: 'حجز الاجتماع الجاي من البطاقة', en: 'Booking the next meeting from the card' },
        paras: [
          {
            ar: 'بدل رسائل «متى يناسبك؟»، العميل يفتح بطاقة المندوب ويشوف أوقاته الفاضية ويحجز. الاجتماع ينزل في تقويم المندوب برابط Teams لما تكون الشركة على Microsoft 365. وتقدر تحدد قوالب الاجتماعات (مثل عرض توضيحي 30 دقيقة) للفريق كله.',
            en: 'Instead of “when suits you?” messages, the client opens the rep’s card, sees free times and books. The meeting lands in the rep’s calendar with a Teams link when the company is on Microsoft 365. You can set meeting templates (like a 30-minute demo) for the whole team.'
          }
        ]
      },
      {
        h2: { ar: 'للمدير', en: 'For the manager' },
        bullets: [
          { ar: 'كل بطاقات الفريق بهوية الشركة من لوحة وحدة.', en: 'All team cards in the company brand from one dashboard.' },
          { ar: 'مندوب جديد؟ تضيفه ويستلم بطاقته نفس اليوم.', en: 'New rep? Add them and they get their card the same day.' },
          { ar: 'مندوب طلع؟ تحذفه وتنتهي بطاقته.', en: 'A rep left? Remove them from the dashboard.' },
          { ar: 'صفحة حجز للفريق يحجز منها العميل مع أي واحد متاح.', en: 'A team booking page where clients book whoever is available.' }
        ],
        paras: []
      }
    ],
    faqs: [
      {
        q: { ar: 'هل الحجز يشتغل مع Outlook الشركة؟', en: 'Does booking work with our company Outlook?' },
        a: { ar: 'إيه، مع Microsoft 365. مسؤول تقنية المعلومات يوافق مرة وحدة وتنربط تقويمات المجموعات اللي تختارها. ربط Google Workspace للشركات قريبًا.', en: 'Yes, with Microsoft 365. Your IT admin approves once and the calendars of the groups you pick connect. Google Workspace for companies is coming soon.' }
      },
      {
        q: { ar: 'نقدر نوحّد شكل البطاقات للفريق كله؟', en: 'Can we keep every card on-brand?' },
        a: { ar: 'إيه، الهوية تتحدد من الشركة، وتقدر تفرّق بين المستويات بألوان فئات مختلفة.', en: 'Yes, the brand is set by the company, and tiers can have different colors.' }
      },
      {
        q: { ar: 'العميل يحتاج تطبيق؟', en: 'Does the client need an app?' },
        a: { ar: 'لا، يمسح الكود بالكاميرا. والحجز من المتصفح.', en: 'No, they scan with the camera, and booking happens in the browser.' }
      }
    ],
    cta: {
      title: { ar: 'خلّ كل مقابلة تنتهي بجهة اتصال وموعد.', en: 'Make every encounter end with a contact and a meeting.' },
      sub: { ar: 'احجز ديمو لفريق المبيعات عندكم.', en: 'Book a demo for your sales team.' },
      btn: { ar: 'احجز ديمو', en: 'Book a demo' },
      to: '/business#book-demo'
    },
    related: ['employee-business-cards', 'meeting-booking', 'business-card-printing-alternative']
  }
  ,

  'cardpass-vs-blinq': {
    slug: 'cardpass-vs-blinq',
    vs: true,
    rival: 'Blinq',
    nav: { ar: 'كارد باس أم Blinq؟', en: 'CardPass vs Blinq' },
    h1: { ar: 'كارد باس أم Blinq؟', en: 'CardPass or Blinq?' },
    h1b: { ar: 'الخيار الأقوى للشركات السعودية.', en: 'The stronger choice for Saudi companies.' },
    lead: {
      ar: 'الاثنين يعطونك بطاقات للموظفين في Apple Wallet و Google Wallet. الفرق إن كارد باس مبني بالعربي للشركات هنا: عملاؤك يحجزون اجتماعهم من بطاقة الموظف، كل مستوى وظيفي ببطاقته، والاشتراك بالريال مع فريق سعودي.',
      en: 'Both give your employees cards in Apple Wallet and Google Wallet. The difference: CardPass is built in Arabic for companies here. Clients book meetings from the employee’s card, every level gets its own card, and you pay in riyals with a Saudi team.'
    },
    price: { usd: '4.99 – 6.99', sar: [19, 26], min: 5 },
    faqs: [
      {
        q: { ar: 'ليش كارد باس أنسب من Blinq للشركات في السعودية؟', en: 'Why is CardPass a better fit than Blinq for Saudi companies?' },
        a: { ar: 'لأنه مبني بالعربي من أول يوم، وعملاؤك يحجزون اجتماعهم مع الموظف من بطاقته مباشرة برابط Teams أو Google Meet، وكل مستوى وظيفي له لون بطاقة يتحدّث لحاله مع الترقية، والاشتراك بالريال مع فريق سعودي يفعّلك خلال أسبوع.', en: 'It is Arabic from day one, clients book meetings with the employee straight from their card with a Teams or Google Meet link, every level gets a card colour that updates with promotions, and you pay in riyals with a Saudi team that gets you live within a week.' }
      },
      {
        q: { ar: 'نقدر ننتقل من Blinq لكارد باس؟', en: 'Can we move from Blinq to CardPass?' },
        a: { ar: 'إيه وبسهولة: صدّر قائمة الموظفين كملف Excel، ارفعه في لوحة كارد باس، وكل موظف توصله دعوة يضيف فيها بطاقته الجديدة لمحفظته. التفعيل كامل خلال أسبوع.', en: 'Yes, easily: export your employee list to Excel, upload it to CardPass, and each employee gets an invite to add their new card to their wallet. Fully live within a week.' }
      },
      {
        q: { ar: 'هل البطاقة تشتغل مع الأندرويد؟', en: 'Does the card work on Android?' },
        a: { ar: 'إيه، الآيفون يضيفها في Apple Wallet والأندرويد في Google Wallet، والعميل يمسح الكود بأي جوال بدون تطبيق.', en: 'Yes, iPhone adds it to Apple Wallet and Android to Google Wallet, and clients scan the code with any phone, no app.' }
      },
      {
        q: { ar: 'من وين جبتوا معلومات Blinq؟', en: 'Where does the Blinq information come from?' },
        a: { ar: 'من صفحة الأسعار الرسمية في موقعهم، راجعناها في أكتوبر 2026. علامة «—» تعني إن الميزة ما هي مذكورة في خططهم المعلنة.', en: 'From the official pricing page on their site, checked in October 2026. A “—” means the feature is not listed in their published plans.' }
      }
    ],
    cta: {
      title: { ar: 'شوف كارد باس على بيانات شركتك.', en: 'See CardPass with your own company.' },
      sub: { ar: 'احجز ديمو وقارن بنفسك.', en: 'Book a demo and compare for yourself.' },
      btn: { ar: 'احجز ديمو', en: 'Book a demo' },
      to: '/business#book-demo'
    },
    related: ['cardpass-vs-hihello', 'employee-business-cards', 'business-card-printing-alternative']
  },

  'cardpass-vs-hihello': {
    slug: 'cardpass-vs-hihello',
    vs: true,
    rival: 'HiHello',
    nav: { ar: 'كارد باس أم HiHello؟', en: 'CardPass vs HiHello' },
    h1: { ar: 'كارد باس أم HiHello؟', en: 'CardPass or HiHello?' },
    h1b: { ar: 'الخيار الأقوى للشركات السعودية.', en: 'The stronger choice for Saudi companies.' },
    lead: {
      ar: 'الاثنين يضيفون بطاقة الموظف في Apple Wallet و Google Wallet. كارد باس يزيد عليها اللي تحتاجه الشركات هنا: واجهة وبطاقة بالعربي، حجز اجتماعات من بطاقة الموظف، فئات بلون لكل مستوى، واشتراك بالريال مع فريق سعودي.',
      en: 'Both add the employee card to Apple Wallet and Google Wallet. CardPass adds what companies here need: Arabic interface and cards, meeting booking from the employee’s card, a colour per level, and riyal billing with a Saudi team.'
    },
    price: { usd: '5 – 6', sar: [19, 23], min: 5 },
    faqs: [
      {
        q: { ar: 'ليش كارد باس أنسب من HiHello للشركات في السعودية؟', en: 'Why is CardPass a better fit than HiHello for Saudi companies?' },
        a: { ar: 'لأنه مبني بالعربي من أول يوم، وعملاؤك يحجزون اجتماعهم مع الموظف من بطاقته برابط Teams أو Google Meet، ولكل مستوى وظيفي لون بطاقة، والاشتراك بالريال مع فريق سعودي يفعّلك خلال أسبوع.', en: 'It is Arabic from day one, clients book meetings with the employee from their card with a Teams or Google Meet link, every level gets its own card colour, and you pay in riyals with a Saudi team that gets you live within a week.' }
      },
      {
        q: { ar: 'نقدر ننتقل من HiHello لكارد باس؟', en: 'Can we move from HiHello to CardPass?' },
        a: { ar: 'إيه: صدّر قائمة الموظفين كملف Excel، ارفعه في لوحة كارد باس، وكل موظف توصله دعوة يضيف فيها بطاقته لمحفظته. التفعيل كامل خلال أسبوع.', en: 'Yes: export your employee list to Excel, upload it to CardPass, and each employee gets an invite to add their card to their wallet. Fully live within a week.' }
      },
      {
        q: { ar: 'هل البطاقة تشتغل مع الأندرويد؟', en: 'Does the card work on Android?' },
        a: { ar: 'إيه، الآيفون يضيفها في Apple Wallet والأندرويد في Google Wallet، والعميل يمسح الكود بأي جوال بدون تطبيق.', en: 'Yes, iPhone adds it to Apple Wallet and Android to Google Wallet, and clients scan the code with any phone, no app.' }
      },
      {
        q: { ar: 'من وين جبتوا معلومات HiHello؟', en: 'Where does the HiHello information come from?' },
        a: { ar: 'من صفحة الأسعار الرسمية في موقعهم، راجعناها في أكتوبر 2026. علامة «—» تعني إن الميزة ما هي مذكورة في خططهم المعلنة.', en: 'From the official pricing page on their site, checked in October 2026. A “—” means the feature is not listed in their published plans.' }
      }
    ],
    cta: {
      title: { ar: 'شوف كارد باس على بيانات شركتك.', en: 'See CardPass with your own company.' },
      sub: { ar: 'احجز ديمو وقارن بنفسك.', en: 'Book a demo and compare for yourself.' },
      btn: { ar: 'احجز ديمو', en: 'Book a demo' },
      to: '/business#book-demo'
    },
    related: ['cardpass-vs-blinq', 'employee-business-cards', 'sales-team-business-cards']
  }
};

export const SEO_PAGE_KEYS = Object.keys(SEO_PAGES);
